import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from './api';
import { useAuth } from './auth';

/**
 * Diagnostic Sessions context.
 *
 * Multi-tab/workstation manager for Diagnostic Chat and Guided Diagnosis.
 * - Each session is a persisted MongoDB document with brand, model, state (JSON), kind.
 * - Sessions are scoped per user (admin can fetch others' via list parameter).
 * - State writes are debounced (800ms) so chat typing / wizard clicks don't spam PATCH.
 * - The "active" session per kind is remembered in localStorage so a refresh keeps the
 *   same tab open.
 */

const SessionsCtx = createContext(null);

const ACTIVE_KEY = 'ev_active_sessions';
const DEBOUNCE_MS = 800;

function readActive() {
  try {
    return JSON.parse(localStorage.getItem(ACTIVE_KEY) || '{}');
  } catch { return {}; }
}
function writeActive(v) {
  try { localStorage.setItem(ACTIVE_KEY, JSON.stringify(v || {})); } catch { /* ignore */ }
}

export function SessionsProvider({ children }) {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);  // active (non-archived) sessions
  const [loading, setLoading] = useState(false);
  const [activeIds, setActiveIds] = useState(() => readActive());
  // pending writes: sid -> { patch: {...}, timer: TimerID }
  const pendingRef = useRef(new Map());

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get('/diagnostic-sessions');
      setSessions(data || []);
    } catch (err) {
      // Silent: the workstation should keep working offline-ish
      // eslint-disable-next-line no-console
      console.warn('[sessions] refresh failed', err);
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => {
    if (user) refresh();
    else { setSessions([]); setActiveIds({}); }
  }, [user, refresh]);

  // Flush all pending debounced writes before unload — prevents losing the last keystroke.
  useEffect(() => {
    const flushAll = () => {
      const m = pendingRef.current;
      m.forEach((entry, sid) => {
        if (entry.timer) clearTimeout(entry.timer);
        if (entry.patch && Object.keys(entry.patch).length) {
          // beacon-style: fire and forget; the browser may close before resolve
          try {
            const url = `${api.defaults.baseURL}/diagnostic-sessions/${sid}`;
            const blob = new Blob([JSON.stringify(entry.patch)], { type: 'application/json' });
            // navigator.sendBeacon does not allow custom headers, so this best-effort works only
            // when the session cookie is enough (we DO use httpOnly cookie auth). On older
            // browsers it's a no-op which is acceptable since axios PATCH below also fires.
            if (navigator.sendBeacon) navigator.sendBeacon(url, blob);
          } catch { /* ignore */ }
          // Also try axios (may not complete on unload but harmless)
          api.patch(`/diagnostic-sessions/${sid}`, entry.patch).catch(() => {});
        }
      });
      m.clear();
    };
    window.addEventListener('beforeunload', flushAll);
    return () => window.removeEventListener('beforeunload', flushAll);
  }, []);

  /** Apply a patch to the in-memory session immediately and schedule a debounced backend PATCH. */
  const scheduleUpdate = useCallback((sid, patch) => {
    if (!sid || !patch) return;
    // Optimistic in-memory merge
    setSessions((arr) => arr.map((s) => s.id === sid ? {
      ...s,
      ...('brand' in patch ? { brand: patch.brand } : {}),
      ...('model' in patch ? { model: patch.model } : {}),
      ...('title' in patch ? { title: patch.title } : {}),
      ...('pinned' in patch ? { pinned: patch.pinned } : {}),
      ...('state' in patch ? { state: { ...(s.state || {}), ...patch.state } } : {}),
      updated_at: new Date().toISOString(),
    } : s));
    // Coalesce pending patches
    const m = pendingRef.current;
    const prev = m.get(sid) || { patch: {}, timer: null };
    if (prev.timer) clearTimeout(prev.timer);
    // Merge: for `state` we deep-merge ourselves so multiple typed keys are preserved
    const mergedState = (patch.state || prev.patch.state)
      ? { ...(prev.patch.state || {}), ...(patch.state || {}) }
      : undefined;
    const mergedPatch = { ...prev.patch, ...patch };
    if (mergedState) mergedPatch.state = mergedState;
    const timer = setTimeout(async () => {
      m.delete(sid);
      try {
        const { data } = await api.patch(`/diagnostic-sessions/${sid}`, mergedPatch);
        // Reconcile authoritative server doc (title may have been derived server-side)
        setSessions((arr) => arr.map((s) => s.id === sid ? data : s));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[sessions] PATCH failed', err);
      }
    }, DEBOUNCE_MS);
    m.set(sid, { patch: mergedPatch, timer });
  }, []);

  /** Force flush of pending writes for a session (used on tab switch / close). */
  const flush = useCallback(async (sid) => {
    if (!sid) return;
    const m = pendingRef.current;
    const entry = m.get(sid);
    if (!entry) return;
    if (entry.timer) clearTimeout(entry.timer);
    m.delete(sid);
    try {
      const { data } = await api.patch(`/diagnostic-sessions/${sid}`, entry.patch);
      setSessions((arr) => arr.map((s) => s.id === sid ? data : s));
    } catch { /* ignore */ }
  }, []);

  const setActive = useCallback((kind, id) => {
    setActiveIds((cur) => {
      const next = { ...cur, [kind]: id || null };
      writeActive(next);
      return next;
    });
  }, []);

  const createSession = useCallback(async (kind, init = {}) => {
    const { data } = await api.post('/diagnostic-sessions', {
      kind,
      brand: init.brand || '',
      model: init.model || '',
      title: init.title || '',
      state: init.state || {},
    });
    setSessions((arr) => [data, ...arr]);
    setActive(kind, data.id);
    return data;
  }, [setActive]);

  const archiveSession = useCallback(async (sid) => {
    await flush(sid);
    await api.patch(`/diagnostic-sessions/${sid}`, { archived: true });
    setSessions((arr) => arr.filter((s) => s.id !== sid));
    // If it was the active one, clear it
    setActiveIds((cur) => {
      const next = { ...cur };
      Object.keys(next).forEach((k) => { if (next[k] === sid) next[k] = null; });
      writeActive(next);
      return next;
    });
  }, [flush]);

  const deleteSession = useCallback(async (sid) => {
    await flush(sid);
    await api.delete(`/diagnostic-sessions/${sid}`);
    setSessions((arr) => arr.filter((s) => s.id !== sid));
    setActiveIds((cur) => {
      const next = { ...cur };
      Object.keys(next).forEach((k) => { if (next[k] === sid) next[k] = null; });
      writeActive(next);
      return next;
    });
  }, [flush]);

  const duplicateSession = useCallback(async (sid) => {
    await flush(sid);
    const { data } = await api.post(`/diagnostic-sessions/${sid}/duplicate`);
    setSessions((arr) => [data, ...arr]);
    setActive(data.kind, data.id);
    return data;
  }, [flush, setActive]);

  const togglePin = useCallback(async (sid) => {
    const s = sessions.find((x) => x.id === sid);
    if (!s) return;
    await flush(sid);
    const { data } = await api.patch(`/diagnostic-sessions/${sid}`, { pinned: !s.pinned });
    setSessions((arr) => arr.map((x) => x.id === sid ? data : x).sort(
      (a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) || b.updated_at.localeCompare(a.updated_at),
    ));
  }, [sessions, flush]);

  /** List archived sessions on demand (separate query so the active tab list stays compact). */
  const listArchived = useCallback(async (kind = null) => {
    const params = new URLSearchParams({ archived: 'true' });
    if (kind) params.set('kind', kind);
    const { data } = await api.get(`/diagnostic-sessions?${params}`);
    return data;
  }, []);

  const unarchive = useCallback(async (sid) => {
    const { data } = await api.patch(`/diagnostic-sessions/${sid}`, { archived: false });
    setSessions((arr) => [data, ...arr.filter((s) => s.id !== sid)]);
    return data;
  }, []);

  const value = useMemo(() => {
    const byKind = (k) => sessions.filter((s) => s.kind === k);
    const activeChat = sessions.find((s) => s.id === activeIds.chat) || null;
    const activeGuided = sessions.find((s) => s.id === activeIds.guided) || null;
    return {
      sessions,
      loading,
      activeIds,
      activeChat,
      activeGuided,
      chatSessions: byKind('chat'),
      guidedSessions: byKind('guided'),
      // mutations
      setActive,
      createSession,
      scheduleUpdate,
      flush,
      archiveSession,
      deleteSession,
      duplicateSession,
      togglePin,
      listArchived,
      unarchive,
      refresh,
    };
  }, [sessions, loading, activeIds, setActive, createSession, scheduleUpdate, flush, archiveSession, deleteSession, duplicateSession, togglePin, listArchived, unarchive, refresh]);

  return <SessionsCtx.Provider value={value}>{children}</SessionsCtx.Provider>;
}

export const useSessions = () => {
  const ctx = useContext(SessionsCtx);
  if (!ctx) throw new Error('useSessions must be used within SessionsProvider');
  return ctx;
};
